// Type imports:
import { isReqError, pushQueryToHistory, turnDateToSimpleTime, _for } from '../../services/utils';
import { AlertReviewHistoryByAlertDataId, AlertStatus, AlertTickData, IAlertConfig, IAlertDataUserInfo } from '../../types/api/AlertTypes';
import { ByTradingDay, Requested } from '../../types/api/Types';
import { ADD_ALERT_REVIEW_HISTORY_ENTRY_FAILURE, ADD_ALERT_REVIEW_HISTORY_ENTRY_SUCCESS, AlertActions, MODIFY_ALERT_CONFIGS_FAILURE, MODIFY_ALERT_CONFIGS_SUCCESS, REQUEST_ALERTS_FAILURE, REQUEST_ALERTS_SUCCESS, REQUEST_ALERTS_WAITING, REQUEST_ALERT_CONFIGS_FAILURE, REQUEST_ALERT_CONFIGS_SUCCESS, REQUEST_ALERT_CONFIGS_WAITING, REQUEST_ALERT_REVIEW_HISTORY_FAILURE, REQUEST_ALERT_REVIEW_HISTORY_SUCCESS, REQUEST_ALERT_REVIEW_HISTORY_WAITING, SET_SELECTED_STATUS } from '../../types/redux/AlertTypes';

export interface IAlertsState {
    alertsByDate: ByTradingDay<Requested<AlertTickData[]>>,
    alertConfigs: Requested<IAlertConfig[]>,
    modifyConfigError: string | null, // TODO: Turn into toast?
    selectedStatus: AlertStatus | null,
    alertReviewHistoryByAlertDataId: AlertReviewHistoryByAlertDataId,
}

const initialState: IAlertsState = {
    alertsByDate: {},
    alertConfigs: undefined,
    modifyConfigError: null,
    selectedStatus: null,
    alertReviewHistoryByAlertDataId: {}
}

export default function(state: IAlertsState = initialState, action: AlertActions): IAlertsState {
    switch(action.type) {
        /* REQUEST HOME ALERTS */
        case REQUEST_ALERTS_WAITING: {
            const { fromDate, toDate } = action.payload;
            const newAlertsByDate = { ...state.alertsByDate };
            _for(fromDate, toDate, (currentDate) => {
                newAlertsByDate[currentDate] = 'waiting';
            });
            const newState: IAlertsState = {
                ...state,
                alertsByDate: newAlertsByDate
            };
            return newState;
        }
        case REQUEST_ALERTS_SUCCESS: {
            const { fromDate, toDate, alerts } = action.payload;
            const newAlertsByDate = { ...state.alertsByDate };

            const alertTickData: Array<AlertTickData> = alerts.map(alert => ({
                ...alert,
                tickdata_type: 'Alert',
                timestamp: alert.ref_timestamp ?? null,
                alert_data_id: alert.ad_id ?? null,
                order_change_volume: alert.volume ?? null
            }))

            _for(fromDate, toDate, (currentDate) => {
                const alertsForCurrentDay = alertTickData.filter(alert =>
                    turnDateToSimpleTime(new Date(alert.ref_timestamp ?? 0)) === currentDate
                );
                newAlertsByDate[currentDate] = alertsForCurrentDay;
            });
            const newState: IAlertsState = {
                ...state,
                alertsByDate: newAlertsByDate
            };
            return newState;
        }
        case REQUEST_ALERTS_FAILURE: {
            const { fromDate, toDate, error } = action.payload;
            const newAlertsByDate = { ...state.alertsByDate };
            _for(fromDate, toDate, (currentDate) => {
                newAlertsByDate[currentDate] = { message: error };
            });
            const newState: IAlertsState = {
                ...state,
                alertsByDate: newAlertsByDate
            };
            return newState;
        }
        /* REQUEST CONFIG ALERTS */
        case REQUEST_ALERT_CONFIGS_WAITING: {
            const newState: IAlertsState = {
                ...state,
                alertConfigs: 'waiting'
            };
            return newState;
        }
        case REQUEST_ALERT_CONFIGS_SUCCESS: {
            const { alertConfigs } = action.payload;
            const newState: IAlertsState = {
                ...state,
                alertConfigs
            };
            return newState;
        }
        case REQUEST_ALERT_CONFIGS_FAILURE: {
            const { error } = action.payload;
            const newState: IAlertsState = {
                ...state,
                alertConfigs: { message: error }
            };
            return newState;
        }
        /* MODIFY ALERT CONFIGS */
        case MODIFY_ALERT_CONFIGS_SUCCESS: {
            const { newAlertConfigs } = action.payload;
            const { alertConfigs } = state;
            if (alertConfigs === undefined || alertConfigs === 'waiting' || isReqError(alertConfigs)) {
                return state;
            }
            const alertConfigsCopy = [...alertConfigs];
            for (let newAlertConfig of newAlertConfigs) {
                const existingIndex = alertConfigsCopy.findIndex(config => config.id === newAlertConfig.id);
                if (existingIndex !== -1) {
                    alertConfigsCopy[existingIndex] = newAlertConfig;
                }
            }
            const newState: IAlertsState = {
                ...state,
                alertConfigs: alertConfigsCopy
            };
            return newState;
        }
        case MODIFY_ALERT_CONFIGS_FAILURE: {
            const { error } = action.payload;
            const newState: IAlertsState = {
                ...state,
                modifyConfigError: error
            };
            return newState;
        }
        /* SET SELECTED STATUS */
        case SET_SELECTED_STATUS: {
            const { status, skipPushingToHistory } = action.payload;
            if (!skipPushingToHistory) {
                pushQueryToHistory('status', status);
            }
            const newState: IAlertsState = {
                ...state,
                selectedStatus: status
            };
            return newState;
        }
        /* REQUEST ALERT REVIEW HISTORY */
        case REQUEST_ALERT_REVIEW_HISTORY_WAITING: {
            const { alertDataId } = action.payload;
            const alertReviewHistoryCopy = {...state.alertReviewHistoryByAlertDataId};
            alertReviewHistoryCopy[alertDataId] = 'waiting';
            const newState: IAlertsState = {
                ...state,
                alertReviewHistoryByAlertDataId: alertReviewHistoryCopy
            };
            return newState;
        }
        case REQUEST_ALERT_REVIEW_HISTORY_SUCCESS: {
            const { alertDataId, alertReviewHistory } = action.payload;
            const alertReviewHistoryCopy = { ...state.alertReviewHistoryByAlertDataId };
            alertReviewHistoryCopy[alertDataId] = alertReviewHistory;
            const newState: IAlertsState = {
                ...state,
                alertReviewHistoryByAlertDataId: alertReviewHistoryCopy
            };
            return newState;
        }
        case REQUEST_ALERT_REVIEW_HISTORY_FAILURE: {
            const { alertDataId, error } = action.payload;
            const alertReviewHistoryCopy = {...state.alertReviewHistoryByAlertDataId};
            alertReviewHistoryCopy[alertDataId] = { message: error };
            const newState: IAlertsState = {
                ...state,
                alertReviewHistoryByAlertDataId: alertReviewHistoryCopy
            };
            return newState;
        }
        /* ADD ALERT REVIEW HISTORY ENTRY */
        case ADD_ALERT_REVIEW_HISTORY_ENTRY_SUCCESS: {
            const { newAlertReviewHistoryEntries } = action.payload;
            type AlertDataIdToReviewHistoryMap = {
                [alertDataId: number]: Array<IAlertDataUserInfo>,
            };
            // Group review history entries by alert_data_id.
            const newAlertReviewHistoryEntriesByAlertIds: AlertDataIdToReviewHistoryMap =
                newAlertReviewHistoryEntries.reduce((map: AlertDataIdToReviewHistoryMap, reviewHistoryEntry: IAlertDataUserInfo) => {
                    const { alert_data_id } = reviewHistoryEntry;
                    if (map[alert_data_id] === undefined) {
                        map[alert_data_id] = [reviewHistoryEntry];
                    } else {
                        map[alert_data_id].push(reviewHistoryEntry);
                    }
                    return map;
                }, {});

            // Update alertReviewHistoryByAlertDataId
            const alertReviewHistoryByAlertDataIdCopy = {...state.alertReviewHistoryByAlertDataId};
            for (let [alertDataId, reviewHistory] of Object.entries(newAlertReviewHistoryEntriesByAlertIds)) {
                alertReviewHistoryByAlertDataIdCopy[parseInt(alertDataId)] = reviewHistory;
            }
            // Update alert tick data.
            const alertsByDateCopy = { ...state.alertsByDate };
            const dateAndAlertInfoTupleArray = Object.entries(alertsByDateCopy);
            const requestedAlerts = dateAndAlertInfoTupleArray.map(tuple => tuple[1]);
            const allAlerts: AlertTickData[] = requestedAlerts.reduce((allAlertsSoFar: AlertTickData[], currentRequestedAlert: Requested<AlertTickData[]>) => {
                if (currentRequestedAlert === undefined || currentRequestedAlert === 'waiting' || isReqError(currentRequestedAlert)) return allAlertsSoFar;
                return allAlertsSoFar.concat(currentRequestedAlert);
            }, []);

            for (let [alertDataId, reviewHistory] of Object.entries(newAlertReviewHistoryEntriesByAlertIds)) {
                const alertDataId_number = parseInt(alertDataId);
                let mostRecentReviewHistoryEntry: IAlertDataUserInfo | null = null;
                for (let historyEntry of reviewHistory) {
                    if (mostRecentReviewHistoryEntry === null || (new Date(historyEntry.comment_timestamp) > new Date(mostRecentReviewHistoryEntry.comment_timestamp))) {
                        mostRecentReviewHistoryEntry = historyEntry;
                    }
                }
                const alertThatMatchesAlertDataId = allAlerts.find(alert => alert.ad_id === alertDataId_number);
                const newAlertToReplaceTheOld = (alertThatMatchesAlertDataId === undefined) ? undefined : { ...alertThatMatchesAlertDataId };
                if (newAlertToReplaceTheOld && mostRecentReviewHistoryEntry) {
                    newAlertToReplaceTheOld.adu_username = mostRecentReviewHistoryEntry.username;
                    newAlertToReplaceTheOld.alert_comment = mostRecentReviewHistoryEntry.alert_comment;
                    newAlertToReplaceTheOld.comment_timestamp = mostRecentReviewHistoryEntry.comment_timestamp;
                    newAlertToReplaceTheOld.status = mostRecentReviewHistoryEntry.status as AlertStatus;
                    if (newAlertToReplaceTheOld.ref_timestamp) {
                        const simpleDateForAlert = turnDateToSimpleTime(new Date(newAlertToReplaceTheOld.ref_timestamp))
                        const currentRequestedAlertsForDate = alertsByDateCopy[simpleDateForAlert];
                        if (currentRequestedAlertsForDate !== undefined && currentRequestedAlertsForDate !== 'waiting' && !(isReqError(currentRequestedAlertsForDate))) {
                            const alertsCopy = [...currentRequestedAlertsForDate];
                            const indexOfNewAlert = alertsCopy.findIndex(alert => alert.ad_id === newAlertToReplaceTheOld.ad_id);
                            alertsCopy[indexOfNewAlert] = newAlertToReplaceTheOld;
                            alertsByDateCopy[simpleDateForAlert] = alertsCopy;
                        }
                    }
                }
            }

            // Return new state.
            const newState: IAlertsState = {
                ...state,
                alertReviewHistoryByAlertDataId: alertReviewHistoryByAlertDataIdCopy,
                alertsByDate: alertsByDateCopy
            };
            return newState;
        }
        case ADD_ALERT_REVIEW_HISTORY_ENTRY_FAILURE: {
            // TODO: Add error toasts.
            const newState: IAlertsState = {
                ...state
            };
            return newState;
        }
        default: {
            return state;
        }
    }
}